import { DEFAULT_SIM_CONFIG, PITCH, simOwnsAerialArm, ENDLESS_HALF_TICKS, predictKickTrajectory, aimElevFor, startKickoff, ControlRouter, BotDriver, BTN, DEFAULT_DIFFICULTY, pickPassTarget, type ModeInfo, type PlayerInput, type MatchState, type SimConfig, type Transport, type EntityId, type TeamIdx, type DifficultyLevel, type TeamSize } from '@fafi/shared';
import { LocalSim } from './game/localSim';
import { NetGame, type JoinIntent } from './game/netGame';
import { KeyboardInput } from './input/keyboardInput';
import { CameraControl } from './input/cameraControl';
import { PointerLockController } from './input/pointerLock';
import { loadBindings } from './input/bindings';
import { ControlsScreen } from './ui/controlsScreen';
import { ceremonyTicksFor } from '@fafi/shared';
import './ui/theme.css'; // D157: brand tokens + kit classes (fonts, :root custom properties, states)
import { FafiScene } from './render/scene';
import { CheatSheet } from './ui/cheatSheet';
import { keyLabel } from './input/actionDefs';
import { makeStores, mergeTutorial } from './storage/storage';
import { button, el } from './ui/dom';
import { BannerOverlay, CeremonyCue, Sfx, bannerFor } from './ui/matchFx';
import { defaultBackNumber } from './render/animatedPlayers';
import { landingRing, aerialCue } from './render/landingMarker';
import { ChargeReticle } from './ui/chargeReticle';
import { Scoreboard, startToast } from './ui/scoreboard';
import { EndScreen } from './ui/endScreen';
import { ScreenFade } from './ui/fade';
import { AimAssist, DEFAULT_AIM_ASSIST, loadAimAssistEnabled, saveAimAssistEnabled } from './input/aimAssist';
import { GoalAimLock, goalFromKeepers, TOUCH_GOAL_LOCK, type GoalDescriptor, type GoalLockOutput } from './input/goalAimLock';
import { KickIntent } from './input/kickIntent';
import { ScenarioReset } from './game/scenarioReset';
import { seatFor, applyBotInputs, restrainBots, HOLD_OPPONENTS_TICKS } from './game/scenarioSeat';
import { ContextMapper, type BallContext, type MapContext } from './input/contextMapper';
import { touchPreferred } from './input/touch/detect';
import { pickTouchAim } from './input/touch/touchInput';
import { loadTouchSettings, saveTouchSettings, TOUCH_OPACITY_MAX, TOUCH_OPACITY_MIN } from './input/touch/touchSettings';
import type { TouchRuntime } from './ui/touchLayer';
import { restartCameraYaw } from './game/restartAim';
import { coneRejection } from './game/coneRejection';
import { SessionKickLatch } from '@fafi/shared';
import { ClipRecorder } from './debug/clipRecorder';
import { WsTransport } from './net/wsTransport';
import { parseFakeLag, wrapFakeLag } from './net/fakeLag';
import { AuthSession } from './api/session';
import { createAccountFlows } from './ui/accountUi';
import { mountHomeScreen, type HomeTeamSummary } from './ui/homeScreen';
import { mountFeedback } from './ui/feedback';
import { difficultyName, difficultyDesc } from './ui/difficultyPicker';
import { DRILLS, drillById, lessonById, LESSONS, newLessonCount, parkOpponentOutfielders } from '@fafi/scenarios';
import { LessonRunner } from './game/lessonRunner';
import { LessonHud } from './ui/lessonHud';
import { lessonPicker } from './ui/lessonPicker';

// ─────────────────────────────────────────────────────────────────────────────
// FAFI client entry. Two modes (M2-S9):
//   • local kickabout (M1):            /            — offline shared-sim sandbox
//   • networked 1-per-side (M2):       /?create     — host a room (share the URL it becomes)
//                                      /?room=CODE  — join one
// Net knobs: ?size=1|2|3|5|11  ?server=ws://…/game  ?fakeLag=100&jitter=30&netseed=42 (§10.6)
// ─────────────────────────────────────────────────────────────────────────────

const container = document.getElementById('app')!;
const hud = document.getElementById('hud')!;
const params = new URLSearchParams(location.search);

// D45 Item 1: a small red toast for uncaught client errors — the next playtest report says the actual
// exception instead of "the screen went blue". Deduped so a per-frame throw can't spam the DOM.
const seenErrors = new Set<string>();
function errorToast(text: string): void {
  const key = text.slice(0, 200);
  if (seenErrors.has(key)) return;
  seenErrors.add(key);
  const el = document.createElement('div');
  el.style.cssText =
    'position:fixed;top:10px;left:50%;transform:translateX(-50%);max-width:80vw;padding:8px 16px;border-radius:8px;' +
    'background:#3a0d0d;color:#ffdede;font:600 13px ui-monospace,monospace;z-index:200;border:1px solid #a33;box-shadow:0 2px 12px rgba(0,0,0,.4)';
  el.textContent = `⚠ ${text}`;
  document.body.appendChild(el);
  setTimeout(() => el.remove(), 8000);
}
window.addEventListener('error', (e) => errorToast(`[fafi] ${e.message}`));
window.addEventListener('unhandledrejection', (e) => errorToast(`[fafi] ${String((e as PromiseRejectionEvent).reason ?? 'unhandled rejection')}`));

/** D45 Item 1: a full-screen fatal error with a back-to-menu link (resume/join gave up). */
function fatalScreen(text: string): void {
  const el = document.createElement('div');
  el.style.cssText =
    'position:fixed;inset:0;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:16px;' +
    'background:rgba(6,14,10,.96);color:#eaffea;font:600 16px ui-monospace,monospace;z-index:210;text-align:center;padding:24px';
  const msg = document.createElement('div');
  msg.textContent = text;
  msg.style.maxWidth = '520px';
  const link = document.createElement('button');
  link.textContent = '⏻ Back to menu';
  link.style.cssText = 'padding:9px 20px;font-size:15px;border:1px solid #6f6;background:#163;color:#e8ffe8;border-radius:6px;cursor:pointer';
  link.onclick = () => window.location.assign(window.location.pathname);
  el.append(msg, link);
  document.body.appendChild(el);
}

/** D65b: a fixed `?debug` net-perf overlay — input-to-photon latency (INTERP delay + snapshot age + RTT),
 *  not frame rate, is the felt "lag" vs the kickabout, so measure it. Off unless `?debug`. */
function makeNetPerfOverlay(): { frame: (dtMs: number, nowMs: number, game: NetGame) => void; pump: (costMs: number) => void } {
  const el = document.createElement('div');
  el.style.cssText =
    'position:fixed;top:8px;left:8px;z-index:250;padding:6px 9px;border-radius:6px;background:rgba(6,14,10,.82);' +
    'color:#9fe6b0;font:600 11px ui-monospace,monospace;white-space:pre;pointer-events:none;line-height:1.45';
  document.body.appendChild(el);
  const frames: number[] = [];
  let pumpCost = 0;
  let lastPaint = 0;
  const p95 = (a: number[]): number => {
    if (a.length === 0) return 0;
    const s = [...a].sort((x, y) => x - y);
    return s[Math.min(s.length - 1, Math.floor(s.length * 0.95))]!;
  };
  return {
    pump: (costMs) => void (pumpCost = costMs),
    frame: (dtMs, nowMs, game) => {
      frames.push(dtMs);
      if (frames.length > 120) frames.shift();
      if (nowMs - lastPaint < 250) return; // repaint 4×/s
      lastPaint = nowMs;
      const age = game.snapshotAgeMs(nowMs);
      el.textContent =
        `frame p95  ${p95(frames).toFixed(1)} ms\n` +
        `pump       ${pumpCost.toFixed(2)} ms\n` +
        `snap age   ${age < 0 ? '—' : `${age.toFixed(0)} ms`}\n` +
        `INTERP     ${game.interpBackMs.toFixed(0)} ms\n` +
        `RTT        ${game.rttMs.toFixed(0)} ms\n` +
        `pred err   ${(game.medianPredictionErrorM() * 100).toFixed(1)} cm`;
    },
  };
}

const kb = new KeyboardInput();
const cam = new CameraControl();

// ── FAFI Touch (FAFI-Touch-Plan §5) ─────────────────────────────────────────
// Sync detection; the DOM layer is a LAZY chunk that installs into `touch` when it arrives — until
// then (and on desktop, forever) every sampling path below reads the keyboard, so the desktop build
// never executes a line of it. `?touch=1|0` forces either way; `?nofs` skips the fullscreen attempt.
const touchActive = touchPreferred(params, {
  ...(typeof window.matchMedia === 'function' ? { matchMedia: window.matchMedia.bind(window) } : {}),
  maxTouchPoints: navigator.maxTouchPoints,
});
let touch: TouchRuntime | null = null;
/** Per-mode actions the layer's buttons drive (assigned by runLocal/runNet once their targets exist). */
const touchHooks: { onSwitch: () => void; onPause: () => void } = { onSwitch: () => {}, onPause: () => {} };
/** Is touch the ACTIVE device right now? A BT keyboard on a tablet flips this off; a tap flips it back. */
const touchDriving = (): boolean => touch !== null && touch.active();
function mountTouchLayer(): void {
  if (!touchActive || touch !== null) return;
  void import('./ui/touchLayer')
    .then((m) => {
      touch = m.mountTouch({
        doc: document,
        storage: localStorage,
        immersive: !params.has('nofs'),
        onAction: (a) => (a === 'switch' ? touchHooks.onSwitch() : touchHooks.onPause()),
      });
    })
    .catch((err: unknown) => errorToast(`[fafi] touch layer failed to load: ${err instanceof Error ? err.message : String(err)}`));
}
if (touchActive) cam.pitch = (35 * Math.PI) / 180; // steeper default view on glass (plan §2.7)
let bindings = loadBindings(localStorage);
// D157: the home screen's how-to-play strip re-reads the live bindings on show(); a rebind while it is
// visible refreshes it through this hook (set only on the menu route — null during a match).
let homeRefresh: (() => void) | null = null;
// D127: the controls screen. The rebinding substrate has shipped since M2 and had no UI; this is it.
// `bindings` is a LET so a rebind takes effect immediately — you can retune keys mid-kickabout and
// test them without a reload, which is the feedback's exact ask.
const controlsScreen = new ControlsScreen({
  doc: document,
  storage: localStorage,
  bindings,
  onChange: (b) => {
    bindings = b;
    homeRefresh?.();
  },
});

// D45 Item 7 / D113: the centre reticle — the shot-charge ring, and the header-ready cue on top of it.
const reticle = new ChargeReticle(document);

// D56 — debug clip extractor: rolling canvas recording + game-state log; F8 downloads both.
// D60: the VIDEO leg (continuous 6 Mbps realtime encode of the canvas) is opt-in via ?clip — the
// always-on recording caused periodic frame hitches on weaker machines. The state LOG stays free.
const clip = new ClipRecorder();
const clipVideo = params.has('clip');
let clipMeta: Record<string, unknown> = {};
window.addEventListener('keydown', (e) => {
  if (e.code === 'F8' && !e.repeat) {
    e.preventDefault();
    void clip.export(clipMeta);
  }
});
const r1 = (n: number): number => Math.round(n * 10) / 10;
let lastClipSampleMs = 0;
function clipSample(view: import('./game/viewState').ViewState, nowMs: number, aimYaw = 0): void {
  if (nowMs - lastClipSampleMs < 150) return;
  lastClipSampleMs = nowMs;
  clip.log.sample({
    t: Math.round(nowMs),
    // D130: the OUTGOING aim. The playtest-4 investigation needed two extra numbers to be conclusive
    // from the log alone — where the player was actually pointing, and at what difficulty. Without this
    // one, "the shot went 90° off" cannot be separated from "he was pointing 90° off".
    aim: Math.round(aimYaw * 100) / 100,
    ph: view.hud.phase,
    sc: view.hud.score,
    ck: view.hud.clockDisplay,
    ball: [r1(view.ball.pos.x), r1(view.ball.pos.y), r1(view.ball.pos.z)],
    tgt: view.passTarget,
    e: view.entities.map((en) => [en.id, r1(en.pos.x), r1(en.pos.y), en.action, (en.hasBall ? 1 : 0) + (en.isControlled ? 2 : 0) + (en.sentOff ? 4 : 0)]),
  });
}

const TEAM_COLORS: [number, number] = [0x3a86ff, 0xef476f];

// D43: display name — custom via the home card, auto-generated otherwise; server moderates it.
function autoName(): string {
  const adj = ['Blitz', 'Turbo', 'Salty', 'Lucky', 'Wild', 'Golden', 'Rapid', 'Cheeky', 'Mighty', 'Sneaky'];
  const animal = ['Fox', 'Falcon', 'Panda', 'Viper', 'Wolf', 'Eagle', 'Lynx', 'Otter', 'Badger', 'Moose'];
  const pick = (a: string[]): string => a[Math.floor(Math.random() * a.length)]!;
  return `${pick(adj)}${pick(animal)}${Math.floor(Math.random() * 90) + 10}`;
}
function playerName(): string {
  let n = localStorage.getItem('fafi.playerName');
  if (!n) {
    n = autoName();
    localStorage.setItem('fafi.playerName', n);
  }
  return n;
}

/**
 * Pointer-lock capture + ESC pause menu (D33). Click the pitch to pin/hide the cursor; the browser's
 * ESC releases it → pause overlay (Continue re-captures — the click IS the gesture; Quit → main page).
 * The overlay locks onto `container` (stable across scene rebuilds), and mouselook is gated on LOCKED.
 */
function wirePointerLock(isPlaying: () => boolean, onFirstLock?: () => void, aimAssist?: { enabled: boolean }, onFeedback?: () => void): PointerLockController {
  let lockedOnce = false;
  const menu = document.createElement('div');
  menu.style.cssText =
    'position:fixed;inset:0;display:none;align-items:center;justify-content:center;background:rgba(4,22,12,.85);z-index:120';
  const panel = document.createElement('div');
  panel.style.cssText =
    'padding:24px 36px;border:1px solid var(--border-hairline);border-radius:12px;background:var(--bg-panel);color:var(--ink-1);font-family:var(--font-ui);text-align:center';
  const title = document.createElement('div');
  title.style.cssText = 'font-size:20px;margin-bottom:14px';
  title.textContent = touchActive ? '⏸ paused — thumbs off' : '⏸ mouse released — paused';
  const mkBtn = (label: string, onClick: () => void, primary = false): HTMLButtonElement => {
    const b = document.createElement('button');
    b.textContent = label;
    b.style.cssText = primary
      ? 'margin:4px 6px;padding:8px 18px;font-size:15px;font-weight:700;border:1px solid var(--accent);background:var(--accent);color:var(--on-accent);border-radius:6px;cursor:pointer'
      : 'margin:4px 6px;padding:8px 18px;font-size:15px;border:1px solid var(--border-strong);background:var(--bg-control);color:var(--ink-1);border-radius:6px;cursor:pointer';
    b.onclick = onClick;
    return b;
  };
  const lock = new PointerLockController({
    isPlaying,
    requestLock: () => {
      if (touchDriving()) {
        lock.lockDenied(); // FAFI Touch (plan §5): no capture on glass — pause rides the ⏸ button
        return;
      }
      // Every failure path must reach lockDenied() — a missed one leaves the camera gated off (D33).
      const req = container.requestPointerLock as (() => unknown) | undefined;
      if (typeof req !== 'function') {
        lock.lockDenied(); // no pointer-lock API at all (older engines)
        return;
      }
      try {
        const r = req.call(container); // void | Promise<void> per browser
        if (r instanceof Promise) r.catch(() => lock.lockDenied());
      } catch {
        lock.lockDenied(); // synchronous throw (e.g. unsupported context)
      }
      // watchdog: some engines fail silently (no rejection, no pointerlockerror) — if we're not
      // LOCKED shortly after asking, fall back to free mouse rather than dead controls
      setTimeout(() => {
        if (lock.state === 'UNLOCKED') lock.lockDenied();
      }, 1500);
    },
    exitLock: () => {
      const ex = document.exitPointerLock as (() => void) | undefined;
      if (typeof ex === 'function') ex.call(document);
    },
    showMenu: (open) => {
      menu.style.display = open ? 'flex' : 'none';
    },
    quit: () => window.location.assign(window.location.pathname), // main page (drop the query/mode)
  });
  panel.append(title, mkBtn('▶ Continue', () => lock.resume(), true));
  if (aimAssist) {
    // D45 Item 3c: aim-assist toggle (persisted; ?noassist still forces off, so re-enabling here is ignored then)
    const aimBtn = mkBtn('', () => {
      aimAssist.enabled = !aimAssist.enabled;
      saveAimAssistEnabled(aimAssist.enabled);
      aimBtn.textContent = `🎯 Aim assist: ${aimAssist.enabled ? 'ON' : 'OFF'}`;
    });
    aimBtn.textContent = `🎯 Aim assist: ${aimAssist.enabled ? 'ON' : 'OFF'}`;
    panel.append(aimBtn);
  }
  if (touchActive) {
    // FAFI Touch T3 (plan §9): overlay opacity + handedness, persisted in fafi.settings.touch
    const t0 = loadTouchSettings(localStorage);
    const opRow = document.createElement('div');
    opRow.style.cssText = 'margin:8px 6px 2px;font-size:13px;color:var(--ink-1)';
    const opLabel = document.createElement('div');
    const opSlider = document.createElement('input');
    opSlider.type = 'range';
    opSlider.min = String(Math.round(TOUCH_OPACITY_MIN * 100));
    opSlider.max = String(Math.round(TOUCH_OPACITY_MAX * 100));
    opSlider.step = '1';
    opSlider.value = String(Math.round(t0.opacity * 100));
    opSlider.style.cssText = 'width:200px;margin-top:4px';
    const opText = (v: number): string => `🎛 Button opacity: ${Math.round(v * 100)}%`;
    opLabel.textContent = opText(t0.opacity);
    opSlider.oninput = () => {
      const v = Number(opSlider.value) / 100;
      touch?.setOpacity(v);
      saveTouchSettings(localStorage, { ...loadTouchSettings(localStorage), opacity: v });
      opLabel.textContent = opText(v);
    };
    opRow.append(opLabel, opSlider);
    const handBtn = mkBtn('', () => {
      const cur = loadTouchSettings(localStorage);
      const handed = cur.handed === 'left' ? 'right' : 'left';
      saveTouchSettings(localStorage, { ...cur, handed });
      touch?.setHanded(handed);
      handBtn.textContent = `🖐 Left-handed layout: ${handed === 'left' ? 'ON' : 'OFF'}`;
    });
    handBtn.textContent = `🖐 Left-handed layout: ${t0.handed === 'left' ? 'ON' : 'OFF'}`;
    const artsBtn = mkBtn('', () => {
      const cur = loadTouchSettings(localStorage);
      const darkArts = !cur.darkArts;
      saveTouchSettings(localStorage, { ...cur, darkArts });
      touch?.setDarkArts(darkArts);
      artsBtn.textContent = `✊ Dive & punch buttons: ${darkArts ? 'ON' : 'OFF'}`;
    });
    artsBtn.textContent = `✊ Dive & punch buttons: ${t0.darkArts ? 'ON' : 'OFF'}`;
    const guideBtn = mkBtn('📖 Touch controls guide', () => window.open('/touch-guide.html', '_blank', 'noopener'));
    panel.append(opRow, handBtn, artsBtn, guideBtn);
  }
  // D163: the pause is the moment a player has an opinion — the box opens over the pause menu.
  if (onFeedback) panel.append(mkBtn('Feedback', () => onFeedback()));
  panel.append(mkBtn('⏻ Quit to main page', () => lock.quit()));
  menu.appendChild(panel);
  document.body.appendChild(menu);
  container.addEventListener('click', () => lock.click());
  document.addEventListener('pointerlockchange', () => {
    const locked = document.pointerLockElement === container;
    lock.lockChange(locked);
    if (locked && !lockedOnce) {
      lockedOnce = true;
      onFirstLock?.(); // D44 Item 10: drop the "click to capture" toast once the mouse is actually captured
    }
  });
  document.addEventListener('pointerlockerror', () => lock.lockDenied());
  window.addEventListener('keydown', (e) => {
    if (e.code === 'Escape') lock.esc();
  });
  cam.setEngageGate(() => lock.mouselookEngaged);
  return lock;
}

// Camera-forward aim (D17): aimYaw = camera yaw — you aim by looking. The mouse drives ONE system
// (mouselook); the hidden OS cursor no longer fights it. (Replaces the M3 pointer-raycast aim; plan
// §13.2/T-CLI-002 amendment queued — the aimModel raycast math stays for gamepad/spectator use.)

function makeScene(teamSize: 1 | 2 | 3 | 5 | 11): FafiScene {
  return new FafiScene(container, {
    pitchLength: PITCH[teamSize].length,
    pitchWidth: PITCH[teamSize].width,
    goalWidth: PITCH[teamSize].goalWidth,
    barH: PITCH[teamSize].barH,
    penaltyBoxDepth: PITCH[teamSize].penaltyBoxDepth,
    penaltyBoxHalfWidth: PITCH[teamSize].penaltyBoxHalfWidth,
    goalAreaDepth: PITCH[teamSize].goalAreaDepth,
    teamColors: [0x3a86ff, 0xef476f],
    ...(touchActive ? { maxPixelRatio: 1.5 } : {}), // FAFI Touch (plan §6): phone GPUs render fewer texels
  });
}

/** D43/D49: shot-arc preview while LMB is held — mirrors the sim's launch + flight AT FULL POWER
 *  (charge no longer scales the kick; the hold is purely aim time). Uses the goal-lock placement when
 *  RMB-aiming, else the free camera aim. */
function updateTrajectory(scene: FafiScene, view: import('./game/viewState').ViewState, cfg: SimConfig, goalOut: GoalLockOutput, freeAimYaw = cam.yaw): void {
  if ((view.hud.chargeButton & BTN.SHOOT) === 0) {
    scene.hideTrajectory();
    return;
  }
  const own = view.entities.find((e) => e.isControlled);
  if (!own) {
    scene.hideTrajectory();
    return;
  }
  // D159: on touch the free aim is the facing-aim ladder, not the camera — the preview must agree.
  const aimY = goalOut.locked ? goalOut.aimYaw : freeAimYaw;
  const aimE = goalOut.locked ? goalOut.aimElev01 : cam.aimElev;
  const origin = { x: own.pos.x + Math.cos(aimY) * 0.45, y: own.pos.y + Math.sin(aimY) * 0.45, z: 0.11 };
  scene.showTrajectory(predictKickTrajectory(cfg, origin, aimY, aimE, view.hud.chargeButton, cfg.actions.kick.CHARGE_CAP));
}

/** D110/D113 (HB §1.4): the landing ring under a hanging ball — both teams read the flight, as in
 *  football — plus the header-ready reticle cue for the ball that is in YOUR strike envelope right now. */
function updateConeCue(scene: FafiScene, view: import('./game/viewState').ViewState, cfg: SimConfig, nowMs: number): void {
  const flick = coneRejection(view, cfg.possession.SHIELD_CONE_DOT); // D132 — render-only (invariant 8)
  if (flick) scene.showConeReject(flick.x, flick.y, flick.facing, nowMs);
}

function updateAerialCues(scene: FafiScene, view: import('./game/viewState').ViewState, cfg: SimConfig, nowMs: number): void {
  const ring = landingRing(view.ball, cfg, view.conditions);
  if (ring) scene.showLanding(ring.x, ring.y, ring.progress01);
  else scene.hideLanding();
  const cue = aerialCue(view, cfg);
  const pulse = 0.5 + 0.5 * Math.sin(nowMs / 90); // a slow shimmer, not a strobe
  reticle.update((view.hud.chargeButton & BTN.SHOOT) !== 0, cue !== null, pulse, view.hud.aerialArmed); // D137/D135
}

/** D137 (§7.2) — what the ball is doing, from the controlled player's point of view. This is the ONLY
 *  thing the context mapper needs to tell Space/E/Ctrl apart, and it comes straight off ViewState so the
 *  local kickabout and the networked game resolve the splits identically. */
function mapContextFor(view: import('./game/viewState').ViewState): MapContext {
  const own = view.entities.find((e) => e.isControlled);
  const poss = view.ball.possessor;
  let ball: BallContext = 'loose';
  if (poss !== null && own) {
    if (poss === own.id) ball = 'mine';
    else ball = view.entities.find((e) => e.id === poss)?.team === own.team ? 'teammate' : 'opponent';
  }
  return { ball, defendingPenalty: view.hud.defendingPenalty };
}

/** Hold-H cheat sheet (D34): one overlay per game session, generated from the live bindings. */
function wireCheatSheet(): void {
  const cs = new CheatSheet(document, bindings);
  window.addEventListener('keydown', (e) => {
    if (e.code === bindings.cheatSheet && e.code !== '' && !e.repeat) cs.show(); // D127: bound, not hard-coded
  });
  window.addEventListener('keyup', (e) => {
    if (e.code === bindings.cheatSheet && e.code !== '') cs.hide();
  });
}

/** D45 Item 8/D35: the shared camera behaviour on a control switch — a keeper gain sweeps the view
 *  downfield (held-W = safety), an outfield switch just follows. Used by both runNet and the kickabout. */
function easeCameraOnSwitch(isGk: boolean, posX: number): void {
  // D134 (✅ user: the pan is liked): the 750 ms ease is COSMETIC and runs while you already have
  // control — only the control transfer itself had to become instant. The constant moved to config.
  if (isGk) cam.easeTo(posX < 0 ? 0 : Math.PI, DEFAULT_SIM_CONFIG.switching.SWITCH_EASE_MS);
}

/** D45 Item 3b: which goal does the controlled team attack? The client has no attackDir on the wire, so
 *  infer it from the keepers — the OPPONENT keeper stands at the opponent goal; fall back to inverting our
 *  own keeper's side. Returns null when no keeper is visible (mid-restart) ⇒ the goal-lock simply won't engage. */
/** D65c: seconds until each team's next red-card return (the max remaining suspension) — for the
 *  scoreboard countdown, so a sent-off player sees when he plays again. */
function returnCountdown(view: import('./game/viewState').ViewState): [number, number] {
  const maxSecs = (team: number): number => view.entities.reduce((m, e) => (e.team === team ? Math.max(m, e.suspendSecs) : m), 0);
  return [maxSecs(0), maxSecs(1)];
}

function deriveOpponentGoal(view: import('./game/viewState').ViewState, ownTeam: number, size: 1 | 2 | 3 | 5 | 11): GoalDescriptor | null {
  const p = PITCH[size];
  const oppGk = view.entities.find((e) => e.isGk && e.team !== ownTeam && !e.sentOff);
  const ownGk = view.entities.find((e) => e.isGk && e.team === ownTeam && !e.sentOff);
  return goalFromKeepers(ownGk ? ownGk.pos.x : null, oppGk ? oppGk.pos.x : null, { length: p.length, goalWidth: p.goalWidth, barH: p.barH });
}

/** D45 Item 3b / D49: run the goal precision-aim mode for a frame and reconcile the camera + reticle.
 *  Engaged by HOLDING the goal-aim button (RMB) while carrying — anywhere on the pitch. Returns the aim
 *  override the input path uses while locked (aimYaw + solved aimElev). Exclusive with the magnet locks. */
function driveGoalAimLock(
  view: import('./game/viewState').ViewState,
  goalLock: GoalAimLock,
  scene: FafiScene,
  cfg: SimConfig,
  size: 1 | 2 | 3 | 5 | 11,
  engageHeld: boolean,
  mouseDx: number,
  mouseDy: number,
  isTaker = false,
): GoalLockOutput {
  const own = view.entities.find((e) => e.isControlled);
  const wasLocked = goalLock.isLocked;
  const goal = own ? deriveOpponentGoal(view, own.team, size) : null;
  const solveElev = (dist: number, z: number): number => aimElevFor(cfg, dist, BTN.SHOOT, cfg.actions.kick.CHARGE_CAP, z); // D49: shots are always full power
  // D130: a placed dead ball has no possessor, so `hasBall` alone meant the precision aim could never
  // engage at a penalty or a free kick — the one moment aim matters most.
  const out = own
    ? goalLock.update({ x: own.pos.x, y: own.pos.y }, cam.yaw, goal, mouseDx, mouseDy, own.hasBall || isTaker, engageHeld, solveElev)
    : { locked: false, aimYaw: cam.yaw, aimElev01: 0, reticle: null };
  if (out.locked && out.reticle && goal) {
    cam.setYawSuspended(true); // the mouse moves the on-plane point, not the camera
    cam.easeTo(out.aimYaw, 140); // gentle follow so the view stays facing the goal
    scene.showGoalReticle(out.reticle.x, out.reticle.y, out.reticle.z, goal.attackSign);
  } else {
    if (wasLocked) cam.yaw = out.aimYaw; // resume free look from the goalAim direction (no snap)
    cam.setYawSuspended(false);
    scene.hideGoalReticle();
  }
  return out;
}

function runLocal(difficulty: DifficultyLevel = DEFAULT_DIFFICULTY): void {
  // D45 Items 2+5: a LIVING 3v3 kickabout — your teammates are bots, opponents are passive dummies
  // (their keeper plays so shooting is real), full switching (Tab), and the D44 aim assist.
  // D46: the kickabout has NO difficulty setting — it always plays at the normal baseline (level 3 =
  // identity). Difficulty is an online-room-only knob (set in the create-room lobby).
  // D128: the kickabout gained practice drills and an honest `?size`. `SIZE = 3` was hard-coded and
  // `?size` was silently ignored, so "try a penalty" had nowhere to go — IP §2.1 named the scenarios
  // package at M0 and it was never built.
  const drill = drillById(params.get('drill') ?? '');
  // D138 (§8.1): a LESSON is a drill with sub-goals. It rides the same local loop, the same restart
  // machinery and the same auto-reset — the training ground is the shipped game with a checklist on it,
  // never a practice-only imitation.
  const lesson = lessonById(params.get('lesson') ?? '');
  const askedSize = Number(params.get('size'));
  const SIZE: TeamSize = lesson ? lesson.teamSize : drill ? drill.teamSize : ([1, 2, 3, 5, 11] as const).includes(askedSize as TeamSize) ? (askedSize as TeamSize) : 3;
  // D144 (playtest 5, report 5): practice has NO CLOCK. `defaultHalfLength(5)` is 90 s, so a lesson
  // reached HALFTIME in under two minutes — which swapped the sides under a drill that had placed
  // players by side, and (before D142) looped. A kickabout and a training ground are rehearsal, not a
  // fixture. Networked matches are unaffected: their mode comes from the server's lobby.
  const mode: ModeInfo = { teamSize: SIZE, offside: false, halfLengthTicks: ENDLESS_HALF_TICKS, overtime: 'DRAW', conditions: 'DRY' };
  const scene = makeScene(SIZE);
  // D146: the lesson's cones/gates actually get drawn. `props` shipped with D139 and had no consumer,
  // so first-touch told you to "dribble through the two cones" on an empty pitch.
  scene.setLessonProps(lesson?.props ?? []);
  if (clipVideo) clip.attach(scene.canvas); // D56 rolling recording — D60: video is ?clip opt-in
  clipMeta = { mode: lesson ? 'lesson' : 'kickabout', size: SIZE, difficulty, ...(drill ? { drill: drill.id } : {}), ...(lesson ? { lesson: lesson.id } : {}) };
  // D138: the objective panel + the runner. The runner never touches the DOM and the HUD never touches
  // MatchState — `keyFor` is the only thing that crosses, and it resolves `{key:action}` copy against
  // the LIVE bindings so a rebind is reflected mid-lesson.
  const lessonHud = lesson ? new LessonHud(document) : null;
  const tutorialStore = makeStores(window.localStorage).tutorial;
  /** D138: praise rides a lightweight banner — a beat of warmth, then gone. */
  const lessonBanner = (text: string): void => {
    const b = el('div', 'position:fixed;left:50%;top:16%;transform:translateX(-50%);z-index:55;padding:10px 18px;border-radius:999px;background:rgba(20,26,34,.9);color:#ffd166;font:600 16px system-ui,sans-serif;pointer-events:none', text);
    document.body.appendChild(b);
    window.setTimeout(() => b.remove(), 1600);
  };
  /** D138: the completion ceremony — the stamp, the chime, and the CTA that makes the chain a loop. */
  const onLessonComplete = (): void => {
    if (!lesson || !lessonRun) return;
    const prev = tutorialStore.load();
    const entry = { at: Date.now(), attempts: lessonRun.attempts, bonus: lessonRun.view().bonus?.done ?? false };
    // progress ONLY ever grows: an earlier completion and a lower attempt count both win (mergeTutorial)
    tutorialStore.save(mergeTutorial(prev, { v: 1, completed: { [lesson.id]: entry } }));
    const next = LESSONS.find((l) => !Object.prototype.hasOwnProperty.call(tutorialStore.load().completed, l.id));
    // D152 (playtest 6, report 2): the completion card is a full-screen overlay with buttons, so the
    // cursor has to come back — exactly as the full-time EndScreen does. Without this the card rendered
    // over a still-locked pointer and neither "Next lesson" nor "Lesson list" could be clicked, which
    // dead-ends the tutorial at the moment it is trying to chain you into the next one.
    lock.suspend();
    const card = el('div', 'position:fixed;inset:0;z-index:70;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:14px;background:rgba(8,11,16,.9);color:#eef3f8;font:16px/1.5 system-ui,sans-serif');
    const stamp = el('div', 'font:800 30px system-ui,sans-serif;color:#5cff9d', `LESSON COMPLETE — ${lesson.title} ✓`);
    const line = el('div', 'opacity:.8', `${lessonRun.attempts} attempt${lessonRun.attempts === 1 ? '' : 's'}${entry.bonus ? '  ·  ★ bonus' : ''}`);
    card.append(stamp, line);
    if (next) card.append(button(`Next lesson → ${next.icon} ${next.title}`, () => window.location.assign(`${window.location.pathname}?lesson=${next.id}`), { primary: true }));
    card.append(button('🎓 Lesson list', () => window.location.assign(`${window.location.pathname}?training`)));
    document.body.appendChild(card);
  };
  const lessonRun = lesson ? new LessonRunner(lesson, (a) => keyLabel((bindings as unknown as Record<string, string>)[a] ?? ''), () => controlled) : null;
  wireCheatSheet();
  const board = new Scoreboard(document, TEAM_COLORS);
  const endScreen = new EndScreen(document, {
    teamColors: TEAM_COLORS,
    onRematch: () => window.location.assign(`${window.location.pathname}?play`), // fresh kickabout
    onNewGame: () => window.location.assign(window.location.pathname),
  });
  const dismissToast = startToast(document, touchActive ? 'Two thumbs to play — ⏸ pauses' : 'Click the pitch to capture your mouse — ESC releases it');
  const cfg = DEFAULT_SIM_CONFIG; // walled ≤5 fences players; the kickoff places everyone (no dev arena bounds)
  const ZERO: PlayerInput = { moveX: 0, moveY: 0, aimYaw: 0, aimElev: 0, buttons: 0, emote: 0 };

  const router = new ControlRouter(cfg);
  const bots = new BotDriver(cfg, 0xb07, difficulty); // D46: ally curve for team 0, enemy for the dummies
  // D143: the scenario says who you are. `bots`/`playerTeam` were declared by every lesson and drill
  // and read by nothing, and the seat was the constant 1 — so keeper-week put you on an outfielder.
  // A 'gk' seat is LOCKED: auto-switching must not quietly hand a keeper lesson an outfielder.
  const scenarioTeam: TeamIdx = lesson?.playerTeam ?? drill?.playerTeam ?? 0;
  const scenarioBots: 'defense' | 'both' = lesson?.bots ?? drill?.bots ?? 'defense';
  // D164: a plain kickabout (no drill, no lesson) fields NO opposing outfielders — the D45 passive
  // dummies read as broken. Walled sizes only: a big-pitch centre kickoff needs an opposition taker.
  const emptyOpposition = !drill && !lesson && SIZE <= 5;
  const scenarioRole = lesson?.playerRole ?? drill?.playerRole ?? 'outfield';
  let controlled: number = seatFor(SIZE, scenarioTeam, scenarioRole);
  router.addSession(0, 0, controlled as EntityId, scenarioRole === 'gk' ? 'locked' : 'auto', 0);
  const aimAssist = new AimAssist(DEFAULT_AIM_ASSIST, !params.has('noassist') && loadAimAssistEnabled());
  const goalLock = new GoalAimLock(touchActive ? TOUCH_GOAL_LOCK : undefined); // D45 Item 3b: goal precision-aim (+ FAFI Touch drag sens)
  const kickIntent = new KickIntent(); // D53: one-touch intent buffer
  const mapper = new ContextMapper(); // D137: held keys × ball context → the outgoing button word
  let mapCtx: MapContext = { ball: 'loose', defendingPenalty: false };
  const kickLatch = new SessionKickLatch(); // D63: a bit held through my own kick is spent until released
  let latchCtrl = -1; // the entity the latched input drove this tick (for the post-step kick detection)
  let latchApplied = 0;
  let goalOut: GoalLockOutput = { locked: false, aimYaw: 0, aimElev01: 0, reticle: null };
  let lastTouchAim = 0; // D159: the facing-aim the last input tick used — the view/preview must agree
  let pendingCycle = false;
  let lastPossessor: number | null = null; // D134: a possession change forces an off-cadence eval

  const inputProvider = (state: MatchState): PlayerInput[] => {
    const tick = state.tick;
    const inputs: PlayerInput[] = state.players.map(() => ZERO);
    const ctrl = (router.routeInput(0, tick, tick) ?? controlled) as EntityId;
    // D45 Item 3b: while goal-locked, the shot is placed via goalAim (aimYaw + solved aimElev), not free look
    let aimY = goalOut.locked ? goalOut.aimYaw : cam.yaw;
    const aimE = goalOut.locked ? goalOut.aimElev01 : cam.aimElev;
    // D159: on touch, aim is DECOUPLED from the camera (which now watches ball/goal) — the facing-aim
    // ladder: stick direction while steering, the opponent goal when attacking, the last run
    // direction when defending. Computed from the sim state this input is for, so it cannot go stale.
    const tdA = touchDriving() ? touch : null;
    const meA = state.players[ctrl];
    if (tdA && !goalOut.locked && meA) {
      const sign = state.attackDir[meA.team] ?? 1;
      const goalYaw = Math.atan2(0 - meA.pos.y, sign * (PITCH[SIZE].length / 2) - meA.pos.x);
      const ballYaw = Math.atan2(state.ball.pos.y - meA.pos.y, state.ball.pos.x - meA.pos.x);
      const attacking = mapCtx.ball === 'mine' || mapCtx.ball === 'teammate';
      aimY = pickTouchAim(tdA.input.stickActive() ? tdA.input.lastMoveWorld() : null, tdA.input.lastMoveWorld(), attacking, goalYaw, ballYaw);
      lastTouchAim = aimY;
    }
    // D137: the buttons come from the context mapper, not from a context-free OR over the bindings.
    // aim = look (D17/D18). On touch the LOOK comes from the follow policy, so aim = run direction —
    // facing-aim (FAFI-Touch-Plan §2.3) through the identical camera-forward contract.
    const td = touchDriving() ? touch : null;
    inputs[ctrl] = td
      ? td.input.sample(cam.yaw, aimY, aimE, mapper.map(td.input.heldCodes(), mapCtx, td.bindings, performance.now()))
      : kb.sample(cam.yaw, aimY, bindings, aimE, mapper.map(kb.heldCodes(), mapCtx, bindings, performance.now()));
    // D53: one-touch intent — a kick tapped BEFORE possession stays armed; the sim volleys on arrival
    // D155: the 4th argument says the SIM owns the SHOOT arm over a flighted ball, so the buffer must not
    // re-emit the bit and swallow PT4-P11's cancel press. It was never passed at either call site.
    inputs[ctrl] = { ...inputs[ctrl]!, buttons: kickIntent.apply(inputs[ctrl]!.buttons, state.ball.possessor === ctrl, tick, simOwnsAerialArm(state.ball, cfg)) };
    // D63: …and a kick bit held since MY OWN kick fired is SPENT, on whatever entity I drive next.
    // (D54 above covers the local hasBall edge; this is the authoritative session-level rule, identical
    // to the server's, so local and online play can never diverge on it.)
    inputs[ctrl] = { ...inputs[ctrl]!, buttons: kickLatch.apply(inputs[ctrl]!.buttons) };
    latchCtrl = ctrl;
    latchApplied = inputs[ctrl]!.buttons;
    // D52: the pass INPUT aims straight AT the ringed mate on the press edge — the ring and the pass
    // then share not just the function (pickPassTarget) but its exact inputs.
    const me = state.players[ctrl];
    const passPressed = me && (inputs[ctrl]!.buttons & ~me.prevButtons) & (BTN.PASS | BTN.THROUGH);
    if (passPressed && state.ball.possessor === ctrl) {
      const t = pickPassTarget(state, ctrl, { x: state.ball.pos.x, y: state.ball.pos.y }, inputs[ctrl]!.aimYaw, cfg.actions.kick.MAX.PASS, cfg);
      if (t) {
        const tp = state.players[t.id]!;
        inputs[ctrl] = { ...inputs[ctrl]!, aimYaw: Math.atan2(tp.pos.y - me.pos.y, tp.pos.x - me.pos.x) };
      }
    }
    // D143: the scenario's own `bots` rule decides who plays. 'defense' keeps the D45 kickabout shape
    // (your side + both keepers, so an attacking drill is not broken up); 'both' is a full live
    // rehearsal. Before this every opposing outfielder was a passive dummy on ZERO input, whatever the
    // lesson asked for — which is why the 1v1 opponent never moved and the free-kick wall never formed.
    // D134: no handover window — the entity is the human's from the assign tick, so he is always held.
    const humanHeld = new Set<EntityId>([ctrl]);
    const humanInput = inputs[ctrl]!;
    applyBotInputs(state, inputs, bots.inputsFor(state, humanHeld, tick), scenarioBots, scenarioTeam);
    // D152: in a LESSON the bots are sparring partners, not opponents playing for a result.
    if (lesson) {
      restrainBots(state, inputs, {
        seat: ctrl,
        playerTeam: scenarioTeam,
        botsShoot: lesson.botsShoot === true,
        holdOpponents: !scenarioReset.hasTouched() && scenarioReset.sinceArm(state) < HOLD_OPPONENTS_TICKS,
      });
    }
    // D58: the designated receiver of MY in-flight pass keeps RUNNING (bot movement) but may not KICK —
    // the D57 brain one-touched the arriving ball away on the claim tick, one tick before
    // switch-at-reception handed me control. The first touch is mine.
    for (const id of router.pendingReceivers()) {
      const inp = inputs[id];
      if (inp) inputs[id] = { ...inp, buttons: inp.buttons & ~(BTN.PASS | BTN.THROUGH | BTN.SHOOT) };
    }
    inputs[ctrl] = humanInput; // belt-and-braces: a human's input is FINAL (D134: from the first tick)
    return inputs;
  };

  const onTick = (state: MatchState, fromTick: number, events: readonly import('@fafi/shared').SimEvent[] = []): void => {
    // D164: the plain kickabout has NO opposing outfielders — you + your bot side vs their keeper.
    // Re-asserted every tick because the rules re-place the full XI at every restart; walled sizes
    // only (≤5), since a big-pitch centre kickoff needs an opposition outfielder to take it.
    if (emptyOpposition) parkOpponentOutfielders(state, scenarioTeam);
    driveDrillReset(state); // D128: re-arm the drill a few seconds after each outcome
    if (lessonRun && !lessonRun.complete) {
      const done = lessonRun.onTick(events, state);
      const line = lessonRun.takePraise();
      if (line) lessonBanner(line);
      if (done) onLessonComplete();
    }
    const lt = state.ball.lastTouch;
    if (lt && lt.tick === fromTick) router.noteTouch(lt.entity, lt.tick); // D43 tick-exact pass-landing
    // D63: my kick just fired ⇒ the bits I was holding are consumed until physically released
    if (lt && lt.tick === fromTick && state.ball.kickedBy === lt.entity && lt.entity === latchCtrl) kickLatch.onKickFired(latchApplied);
    // D134: the local loop mirrors the server's edge trigger exactly, so the kickabout and the online
    // game can never disagree about WHEN control moves.
    const possChanged = state.ball.possessor !== lastPossessor;
    lastPossessor = state.ball.possessor;
    const cadence = state.tick % cfg.switching.EVAL_PERIOD === 0;
    if (!pendingCycle && !possChanged && !cadence && !router.hasPendingResolution()) return; // D50: received pass ⇒ evaluate NOW
    pendingCycle = false;
    for (const rec of router.evaluate(state, fromTick)) {
      if (rec.sessionSlot !== 0) continue;
      if (rec.entityId !== controlled) {
        controlled = rec.entityId;
        const p = state.players[rec.entityId];
        if (p) easeCameraOnSwitch(p.isGk, p.pos.x);
      }
    }
  };

  // D128: a drill sets the board INSTEAD of the kickoff, and re-applies after each outcome so you can
  // rehearse the same situation. It runs the shipped restart machinery — not a practice-only imitation.
  // D138: a tight retry loop is the biggest feel lever a trainer has, so a LESSON re-arms in ~2 s rather
  // than the drill's 4. Ticks, not wall time, so it cannot drift with the frame rate.
  // D145: `resetOn` finally has a reader. A drill keeps the plain dead-ball rule; a lesson may declare
  // 'outcome', which also ends a rep that fizzled out — on a walled pitch (≤ 5-a-side is cage football)
  // a missed shot never leaves play, so the dead-ball rule alone could never re-arm the volley lesson.
  const scenarioReset = new ScenarioReset((lesson ? 2 : 4) * 30, lesson?.resetOn ?? 'dead-ball', controlled);
  const applyDrill = (s: MatchState): void => {
    if (lesson) lesson.setup(s, cfg);
    else if (drill) drill.apply(s, cfg);
    else {
      startKickoff(s, 0, [], cfg);
      if (emptyOpposition) parkOpponentOutfielders(s, scenarioTeam); // D164: parked from the first frame
    }
    scenarioReset.arm(s); // D142: remember the situation, so it is not mistaken for an outcome
  };
  // D128: the auto-reset loop. A drill is for repetition, so an outcome must hand you the same
  // situation again rather than leaving you jogging around an empty pitch. "Outcome" is: the ball went
  // dead (a goal, or any restart the drill did not itself set up) and stayed dead for RESET_DELAY.
  // Deterministic — it is a function of the sim's own tick count, not of wall time.
  // D142: the predicate lives in `game/scenarioReset` — a scenario's OWN pending set piece is the
  // situation being rehearsed, not an outcome. See that file for the ceremony-vs-re-arm table; the
  // short version is that `phase !== 'PLAY'` made penalty-school unplayable and looped halftime.
  const driveDrillReset = (s: MatchState): void => {
    if (!drill && !lesson) return;
    // D153: a FAILED attempt serves the situation again at once — a missed penalty must not turn into a
    // full match, and a 1v1 you have lost is over the moment you lose it.
    const failed = lessonRun?.takeFailure() === true;
    if (!failed && !scenarioReset.shouldReset(s)) return;
    applyDrill(s);
    lessonRun?.noteAttempt(); // …and the CHECKLIST is untouched: attempts are free, progress is permanent
  };
  const sim = new LocalSim(cfg, mode, 0x1234abcd, inputProvider, { onInit: applyDrill, onTick });
  const lock = wirePointerLock(() => !endScreen.visible, dismissToast, aimAssist, () => feedbackUi.open('pause')); // in play until the full-time screen owns the cursor
  mountTouchLayer(); // FAFI Touch: lazy layer for play routes (a no-op on desktop)
  touchHooks.onSwitch = () => {
    router.requestSwitch(0, 'cycle');
    pendingCycle = true;
  };
  touchHooks.onPause = () => lock.esc();
  // Tab = smart cycle-switch (the local shared ControlRouter — D44 relevance ranking comes for free)
  window.addEventListener('keydown', (e) => {
    if (e.code === bindings.switchPlayer && e.code !== '') { // D127
      e.preventDefault();
      if (!e.repeat) {
        router.requestSwitch(0, 'cycle');
        pendingCycle = true;
      }
    }
  });
  // debug handle (§16.3): lets E2E/visual probes inspect the scene graph (e.g. rig pose checks)
  (window as unknown as { fafi: object }).fafi = { scene, controlled: () => controlled, state: () => sim.current, goal: () => goalOut };

  let last = performance.now();
  const frame = (now: number): void => {
    const dt = (now - last) / 1000;
    last = now;
    cam.frame(now); // advance any switch camera ease
    sim.advance(dt);
    const tt = touchDriving() ? touch : null;
    const view = sim.view(controlled, tt ? lastTouchAim : cam.yaw); // aim drives the pass-target highlight (D34/D159)
    if (tt) {
      cam.takeFrameDx(); // drain the mouse accumulators so a device swap can't burst stale deltas
      cam.takeFrameDy();
    }
    const mdx = tt ? tt.input.takeAimDx() : cam.takeFrameDx();
    const mdy = tt ? tt.input.takeAimDy() : cam.takeFrameDy();
    mapCtx = mapContextFor(view); // D137 — read once per frame, used by the next input sample
    touch?.frame(now, mapCtx, { charging: view.hud.chargeButton !== 0, chargeButton: view.hud.chargeButton, armed: kickIntent.armed });
    const lr = sim.current.restart; // D130: the designated taker aims without possessing
    const engageHeld = tt ? tt.input.isHeld(tt.bindings.shoot) : kb.isHeld(bindings.shoot);
    goalOut = driveGoalAimLock(view, goalLock, scene, cfg, SIZE, engageHeld, mdx, mdy, lr !== null && lr.taker === controlled); // D137 §7.3: the precision aim rides the SHOOT hold
    if (!goalOut.locked) {
      if (tt) {
        // D159: the camera watches the BALL, and the opponent GOAL while the controlled team attacks —
        // the stick never steers it (the playtest's "it turns too quickly" was the v1 run-chase).
        const own = view.entities.find((e) => e.isControlled);
        const attackSign = own ? (own.team === 0 ? view.attackDir0 : -view.attackDir0) : 1;
        const deadBallYaw =
          own && lr !== null && (view.hud.phase === 'RESTART_PENDING' || view.hud.phase === 'KICKOFF')
            ? restartCameraYaw({
                kind: lr.kind,
                spot: lr.spot,
                taker: lr.taker,
                controlled,
                ownPos: { x: own.pos.x, y: own.pos.y },
                attackSign,
                pitchLength: PITCH[SIZE].length,
              })
            : null;
        cam.yaw = tt.cam.frame({
          nowMs: now,
          dtSec: dt,
          yaw: cam.yaw,
          easing: cam.isEasing,
          manualDx: tt.input.takeCamDx(),
          recenter: tt.input.takeRecenter(),
          possession: mapCtx.ball, // D161: the camera latches attack state itself (loose holds)
          goalYaw: own ? Math.atan2(0 - own.pos.y, attackSign * (PITCH[SIZE].length / 2) - own.pos.x) : null,
          ballYaw: own ? Math.atan2(view.ball.pos.y - own.pos.y, view.ball.pos.x - own.pos.x) : null,
          ballDist: own ? Math.hypot(view.ball.pos.x - own.pos.x, view.ball.pos.y - own.pos.y) : null,
          deadBallYaw,
        });
      } else {
        cam.yaw = aimAssist.update(view, cam.yaw, mdx, dt, now); // D44 Item 8 (exclusive with goal-lock)
      }
    }
    scene.cameraYaw = cam.yaw;
    scene.cameraPitch = cam.pitch;
    updateTrajectory(scene, view, cfg, goalOut, tt ? lastTouchAim : cam.yaw);
    updateAerialCues(scene, view, cfg, now);
    updateConeCue(scene, view, cfg, now); // D132
    if (lessonHud && lessonRun) lessonHud.update(lessonRun.view()); // D138
    scene.update(view);
    scene.render();
    clipSample(view, now, goalOut.locked ? goalOut.aimYaw : tt ? lastTouchAim : cam.yaw); // D56/D130/D159
    board.update(view.hud.score, view.hud.clockDisplay, view.hud.phase, view.hud.stamina, [
        view.entities.filter((e) => e.team === 0 && e.sentOff).length,
        view.entities.filter((e) => e.team === 1 && e.sentOff).length,
      ], returnCountdown(view)); // D41 scoreboard + D55 red cards + D65c return countdown
    if (view.hud.phase === 'FULLTIME') {
      if (!endScreen.visible) {
        endScreen.show(view.hud.score);
        lock.suspend(); // release the cursor so the end-screen buttons are clickable (D44 Item 9)
      }
    } else {
      endScreen.hide();
      lock.unsuspend(); // rematch/next kickoff — normal click-to-capture resumes
    }
    requestAnimationFrame(frameSafe);
  };
  // D62: the frame pump must be unkillable. A single throw anywhere in the body used to skip the
  // trailing requestAnimationFrame and end rendering PERMANENTLY — the canvas froze on its last frame
  // while the network pump, the reconnect overlay and the status marker kept running, so the tab looked
  // alive and the connection still read PLAYING. Indistinguishable, from the seat, from the netcode
  // freeze this whole programme is about.
  const frameSafe = (now: number): void => {
    try {
      frame(now);
    } catch (err) {
      errorToast(`[fafi] frame: ${err instanceof Error ? err.message : String(err)}`);
      requestAnimationFrame(frameSafe); // the body threw before it could schedule — keep the pump alive
    }
  };
  requestAnimationFrame(frameSafe);
  window.addEventListener('resize', () => scene.resize());
}

function runNet(override?: { join?: JoinIntent; clubTeamId?: string }): void {
  const sizeParam = Number(params.get('size') ?? 5);
  const teamSize = ([1, 2, 3, 5, 11] as const).includes(sizeParam as 1 | 2 | 3 | 5 | 11) ? (sizeParam as 1 | 2 | 3 | 5 | 11) : 5;
  const join: JoinIntent =
    override?.join ??
    (params.has('room') ? { kind: 'join', code: (params.get('room') ?? '').toUpperCase() } : { kind: 'create', teamSize });

  const makeSocket = (): Transport => {
    const proto = location.protocol === 'https:' ? 'wss' : 'ws';
    const url = params.get('server') ?? `${proto}://${location.host}/game`;
    const ws = new WsTransport(url);
    const fl = parseFakeLag(params);
    return fl ? wrapFakeLag(ws, fl) : ws;
  };

  const authToken = session.getToken();
  const banner = new BannerOverlay();
  const ceremonyCue = new CeremonyCue(document); // D123: countdown + "wait for the whistle"
  const sfx = new Sfx(() => new AudioContext()); // injected here — main is the composition root (T-ARCH-004)
  const fade = new ScreenFade(document); // D44 Items 4+5: restart fade-through
  const aimAssist = new AimAssist(DEFAULT_AIM_ASSIST, !params.has('noassist') && loadAimAssistEnabled()); // D44 Item 8: aim soft-lock (?noassist off)
  const goalLock = new GoalAimLock(touchActive ? TOUCH_GOAL_LOCK : undefined); // D45 Item 3b: goal precision-aim (+ FAFI Touch drag sens)
  const mapper = new ContextMapper(); // D137: held keys × ball context → the outgoing button word
  let mapCtx: MapContext = { ball: 'loose', defendingPenalty: false };
  let goalOut: GoalLockOutput = { locked: false, aimYaw: 0, aimElev01: 0, reticle: null };
  let ownPos = { x: 0, y: 0 }; // latest controlled-entity position, for the restart camera re-orient
  let ownAttackSign = 1; // D130: which goal the controlled player attacks, for the goalward camera seed
  let lastBallPos = { x: 0, y: 0 }; // D159: for the touch facing-aim + ball-focused camera
  let lastTouchAim = 0; // D159: the facing-aim the last sample used — preview/log must agree
  const board = new Scoreboard(document, TEAM_COLORS);
  const endScreen = new EndScreen(document, {
    teamColors: TEAM_COLORS,
    onRematch: () => game.voteRematch(), // D41: room-scoped — everyone who presses lands in the same next match
    onNewGame: () => window.location.assign(window.location.pathname),
  });
  const dismissToast = startToast(document, touchActive ? 'Two thumbs to play — ⏸ pauses' : 'Click the pitch to capture your mouse — ESC releases it');
  const game = new NetGame({
    makeSocket,
    join,
    playerName: playerName(), // D43: shown over your head to other players (server-moderated)
    onRematchState: (votes, needed) => endScreen.setVotes(votes, needed),
    onFatal: (m) => fatalScreen(m), // D45 Item 1: resume/join watchdog gave up → error screen (no more freeze)
    ...(authToken ? { authToken } : {}),
    ...(override?.clubTeamId ? { clubTeamId: override.clubTeamId } : {}),
    sampleInput: () => {
      // D137: the context mapper owns the button word (§7.2); D45's goal-lock still overrides the aim.
      const td = touchDriving() ? touch : null;
      let aimY = goalOut.locked ? goalOut.aimYaw : cam.yaw;
      const aimE = goalOut.locked ? goalOut.aimElev01 : cam.aimElev;
      if (td && !goalOut.locked) {
        // D159: the touch facing-aim ladder (stick dir → attack goal → last run dir → ball)
        const goalYaw = Math.atan2(0 - ownPos.y, ownAttackSign * (PITCH[sceneSize as 1 | 2 | 3 | 5 | 11].length / 2) - ownPos.x);
        const ballYaw = Math.atan2(lastBallPos.y - ownPos.y, lastBallPos.x - ownPos.x);
        const attacking = mapCtx.ball === 'mine' || mapCtx.ball === 'teammate';
        aimY = pickTouchAim(td.input.stickActive() ? td.input.lastMoveWorld() : null, td.input.lastMoveWorld(), attacking, goalYaw, ballYaw);
        lastTouchAim = aimY;
      }
      return td
        ? td.input.sample(cam.yaw, aimY, aimE, mapper.map(td.input.heldCodes(), mapCtx, td.bindings, performance.now()))
        : kb.sample(cam.yaw, aimY, bindings, aimE, mapper.map(kb.heldCodes(), mapCtx, bindings, performance.now()));
    },
    onEvent: (e, tick, idx) => {
      // D56: events ride the debug log ('at' — SimEvents own 't'). D119: `tick`/`idx` are the SERVER's
      // own key for this event; `at` is only when this client processed it. Two events 37 ms apart could
      // be one tick or two bunched by jitter — with the tick, "same tick" vs "next tick" is not a guess.
      clip.log.event({ tick, idx, at: Math.round(performance.now()), ...e });
      // D83: a foul banner names WHO fouled WHOM (jersey back numbers); restart banners ride the ceremony.
      const nameOf = (id: number): string => `#${defaultBackNumber({ id, isGk: teamSize > 0 && id % teamSize === 0 })}`;
      // D123: the countdown ring — a restart award starts it, WHISTLE{RESTART} ends it.
      if (e.t === 'OUT_OF_PLAY') ceremonyCue.begin(ceremonyTicksFor(e.restart, DEFAULT_SIM_CONFIG), performance.now());
      if (e.t === 'WHISTLE' && e.kind === 'RESTART') ceremonyCue.end();
      const text = bannerFor(e, nameOf, game.controlledEntity); // D123: "YOU take it" when the restart is yours
      const cerMs = (DEFAULT_SIM_CONFIG.rules.CEREMONY_TICKS / 30) * 1000;
      if (text) banner.show(text, performance.now(), e.t === 'FOUL' || e.t === 'OUT_OF_PLAY' ? cerMs : 2200);
      sfx.forEvent(e); // D18 procedural SFX
      // D44 Items 4+5: any restart (out-of-play, or a foul with a restart) fades through black and,
      // while black, points the camera at the restart spot — hiding the D38 instant teleport. Never on
      // GOAL (the celebration already provides the beat).
      // D130: …and when the restart is YOURS and shootable, the camera seeds at the GOAL instead of the
      // spot. The camera IS the aim (cam.yaw feeds aimYaw), and the sim already faces the taker goalward
      // — this is the render finally agreeing with it.
      const spot = e.t === 'OUT_OF_PLAY' || (e.t === 'FOUL' && e.restart) ? e.spot : null;
      if (spot) {
        const kind = e.t === 'OUT_OF_PLAY' ? e.restart : 'FREE_KICK';
        const taker = e.t === 'OUT_OF_PLAY' ? e.taker : null;
        const yaw = restartCameraYaw({ kind, spot, taker, controlled: game.controlledEntity, ownPos, attackSign: ownAttackSign, pitchLength: PITCH[sceneSize as 1 | 2 | 3 | 5 | 11].length });
        fade.fadeThrough(() => cam.easeTo(yaw, 0));
      }
    },
    onControlledChange: (info) => {
      // D35/D45: gaining the keeper sweeps the camera downfield so held-W means SAFETY (shared helper).
      easeCameraOnSwitch(info.isGk, info.posX);
    },
    onCode: (code) => {
      if (params.get('room') === code) return;
      const u = new URL(location.href);
      u.searchParams.delete('create');
      u.searchParams.set('room', code);
      history.replaceState(null, '', u); // the address bar IS the invite link
    },
  });

  const lock = wirePointerLock(() => game.inMatch() && !endScreen.visible, dismissToast, aimAssist, () => feedbackUi.open('pause')); // lobby + full-time need the cursor
  wireCheatSheet();
  mountTouchLayer(); // FAFI Touch: lazy layer for play routes (a no-op on desktop)
  touchHooks.onSwitch = () => game.requestCycle();
  touchHooks.onPause = () => lock.esc();

  // D18: Tab = cycle-switch (server decides the nearest-to-ball order); hold-Tab stays free for the scoreboard
  window.addEventListener('keydown', (e) => {
    if (e.code === bindings.switchPlayer && e.code !== '') { // D127
      e.preventDefault();
      if (!e.repeat) game.requestCycle();
    }
  });

  // D65b: `?debug` net perf overlay — input-to-photon latency, not frame rate, is the felt "lag" (INTERP
  // delay + snapshot age + RTT). Instrument it so a change is measured, not guessed. Zero cost unless on.
  const perf = params.has('debug') ? makeNetPerfOverlay() : null;

  // D117: the input pump runs FROM THE RENDER FRAME (see frame() below), not off a free-running 30 Hz
  // interval. The interval advanced prediction — and so the follow-camera anchored to the predicted
  // player, and so the whole scene — out of phase with the display, and sampled input (mouse aim
  // included) at 30 Hz. Everything inside pump() is time- or tick-gated, so calling it per frame only
  // stamps on a real tick boundary; the extra calls are no-ops.
  //
  // The interval survives as a HEARTBEAT: §13.2 requires pings + inputs to keep flowing when a hidden
  // tab stops rAF entirely. Browsers clamp background timers to ≥1 s regardless, so a slower heartbeat
  // loses nothing there and costs ~10 no-op calls/s in the foreground.
  const PUMP_HEARTBEAT_MS = 100;
  const pump = (nowMs: number): void => {
    const t0 = performance.now();
    game.pump(nowMs);
    perf?.pump(performance.now() - t0);
  };
  setInterval(() => pump(performance.now()), PUMP_HEARTBEAT_MS);

  // debug handle (§16.3): E2E reads this (T-E2E-001 snapshots, T-E2E-002 entity, T-E2E-003 prediction error)
  (window as unknown as { fafi: object }).fafi = {
    game,
    snapshots: () => game.snapshotsApplied,
    entity: () => game.controlledEntity,
    predErrorM: () => game.medianPredictionErrorM(),
    state: () => game.connState,
    inMatch: () => game.inMatch(),
  };

  // DOM markers (T-E2E-002 waits on these, no sleeps) + a visible "reconnecting" overlay (§13.1).
  const marker = document.createElement('div');
  marker.id = 'fafi-status';
  marker.style.cssText = 'position:fixed;top:4px;left:4px;font:11px ui-monospace,monospace;color:#7fb;opacity:.7;z-index:90';
  document.body.appendChild(marker);
  const reconnectEl = document.createElement('div');
  reconnectEl.id = 'fafi-reconnecting';
  reconnectEl.textContent = '⟳ reconnecting…';
  reconnectEl.style.cssText =
    'position:fixed;inset:0;display:none;align-items:center;justify-content:center;background:rgba(4,22,12,.6);color:#ffd;font:20px ui-monospace,monospace;z-index:110';
  document.body.appendChild(reconnectEl);

  // Drive the status marker + reconnect overlay off a timer (not rAF): a backgrounded tab throttles rAF, but
  // the E2E DOM-state waits + the reconnect overlay must stay live regardless (§13.1/§13.2).
  setInterval(() => {
    reconnectEl.style.display = game.connState === 'RECONNECTING' ? 'flex' : 'none';
    marker.dataset['state'] = String(game.connState);
    marker.dataset['entity'] = String(game.controlledEntity);
    marker.dataset['snapshots'] = String(game.snapshotsApplied);
    marker.dataset['inmatch'] = String(game.inMatch());
  }, 100);

  // ── two-step lobby overlay (M9): counter + team select; host picks the size + starts (solo-start OK) ──
  // built with DOM APIs only (innerHTML is banned, §15.1). Rebuilt only when the lobby state changes.
  const lobbyEl = document.createElement('div');
  lobbyEl.style.cssText =
    'position:fixed;inset:0;display:none;align-items:center;justify-content:center;background:rgba(4,22,12,.92);color:#e8ffe8;font-family:ui-monospace,monospace;z-index:100;text-align:center';
  document.body.appendChild(lobbyEl);
  const div = (style: string, text?: string): HTMLDivElement => {
    const d = document.createElement('div');
    d.style.cssText = style;
    if (text !== undefined) d.textContent = text;
    return d;
  };
  const button = (label: string, active: boolean, big: boolean, onClick: () => void): HTMLButtonElement => {
    const b = document.createElement('button');
    b.textContent = label;
    const border = active || big ? '#6f6' : '#385';
    const bg = big ? '#194' : active ? '#163' : '#0a1f14';
    b.style.cssText = `margin:2px;padding:${big ? '10px 20px' : '6px 12px'};font-size:${big ? '16px' : '14px'};border:1px solid ${border};background:${bg};color:#e8ffe8;border-radius:${big ? '8px' : '6px'};cursor:pointer`;
    b.onclick = onClick;
    return b;
  };
  const sizes = [1, 2, 3, 5, 11];
  let lobbySig = '';
  const renderLobby = (): void => {
    if (game.inMatch()) {
      if (lobbyEl.style.display !== 'none') lobbyEl.style.display = 'none';
      return;
    }
    const L = game.lobby();
    lobbyEl.style.display = 'flex';
    const sig = JSON.stringify([L.code, L.players, L.teamSize, L.teams, L.voice, L.difficulty, L.isHost, L.yourTeam, game.lastError]);
    if (sig === lobbySig) return; // only rebuild the DOM when the lobby state actually changes
    lobbySig = sig;

    const panel = div('min-width:340px;padding:24px 32px;border:1px solid #274;border-radius:12px;background:#08160e');
    panel.append(div('font-size:28px;font-weight:700;letter-spacing:2px', '⚽ FAFI'));
    if (!L.code) {
      // no room code yet ⇒ the socket hasn't confirmed the room (server starting or unreachable)
      panel.append(div('margin:16px 0;opacity:.85', 'connecting to the game server…'));
      if (game.lastError) panel.append(div('margin-top:8px;color:#f88', `⚠ ${game.lastError}`));
      lobbyEl.replaceChildren(panel);
      return;
    }
    panel.append(div('margin:8px 0;opacity:.8', `Room ${L.code} — invite a friend:`));
    const inviteUrl = `${location.origin}${location.pathname}?room=${L.code}`;
    const copyBtn = button('📋 Copy invite link', false, false, () => {
      void navigator.clipboard?.writeText(inviteUrl).then(
        () => {
          copyBtn.textContent = '✓ Copied!';
          setTimeout(() => (copyBtn.textContent = '📋 Copy invite link'), 1500);
        },
        () => (copyBtn.textContent = inviteUrl), // clipboard blocked ⇒ show the URL to copy manually
      );
    });
    const copyRow = div('margin:6px 0');
    copyRow.append(copyBtn);
    panel.append(
      copyRow,
      div('margin:8px 0;font-size:20px', `${L.players} player${L.players === 1 ? '' : 's'} in the lobby`),
      div('margin:10px 0', `Pick your side (${L.teamSize} per side):`),
    );
    const sideRow = div('');
    sideRow.append(
      button(`Blue ${L.teams[0]}/${L.teamSize}`, L.yourTeam === 0, false, () => game.requestTeam(0)),
      button(`Red ${L.teams[1]}/${L.teamSize}`, L.yourTeam === 1, false, () => game.requestTeam(1)),
    );
    panel.append(sideRow);
    if (L.isHost) {
      // each control is a cohesive group: its label immediately followed by its row (D46: the difficulty
      // label used to float above the team-size buttons — confusing; now name+desc sit UNDER the boxes).
      const lvl = L.difficulty as DifficultyLevel;
      const sizeRow = div('');
      for (const s of sizes) sizeRow.append(button(String(s), s === L.teamSize, false, () => game.requestMode(s)));
      panel.append(div('margin:14px 0 6px', 'Team size:'), sizeRow);

      const voiceRow = div('');
      voiceRow.append(
        button('🔊 On', L.voice, false, () => game.requestVoice(true)),
        button('🔇 Off', !L.voice, false, () => game.requestVoice(false)),
      );
      panel.append(div('margin:14px 0 6px', 'Proximity voice:'), voiceRow);

      // D46 Item 4: difficulty — number boxes, then the SELECTED level's name + description directly below.
      const diffRow = div('');
      for (const d of [1, 2, 3, 4, 5]) diffRow.append(button(String(d), d === L.difficulty, false, () => game.requestDifficulty(d)));
      const diffCopy = div('margin:6px 0 0');
      diffCopy.append(div('font-size:14px;font-weight:600', difficultyName(lvl)), div('font-size:12px;opacity:.8;margin-top:2px', difficultyDesc(lvl)));
      panel.append(div('margin:14px 0 6px', 'Difficulty:'), diffRow, diffCopy);

      const startRow = div('margin:16px 0 0');
      startRow.append(button('▶ START (vs bots)', false, true, () => game.requestStart()));
      panel.append(startRow);
    } else {
      const lvl = L.difficulty as DifficultyLevel;
      panel.append(div('margin:12px 0 0;opacity:.8', `Proximity voice: ${L.voice ? 'on 🔊' : 'off 🔇'}`));
      panel.append(div('margin:6px 0 2px;opacity:.8', `Difficulty: ${difficultyName(lvl)}`));
      panel.append(div('margin:0 0 6px;font-size:12px;opacity:.6', difficultyDesc(lvl)));
      panel.append(div('margin:16px 0;opacity:.8', 'waiting for the host to start…'));
    }
    if (game.lastError) panel.append(div('margin-top:10px;color:#f88', `⚠ ${game.lastError}`));
    lobbyEl.replaceChildren(panel);
  };

  // scene size follows the room's mode; the host picks it, joiners discover it from the first Welcome.
  // Until then render the requested/default size (cheap; rebuild once if the server says otherwise).
  let scene = makeScene(teamSize);
  if (clipVideo) clip.attach(scene.canvas); // D56 rolling recording — D60: video is ?clip opt-in
  // D130: the ROOM DIFFICULTY belongs in the clip meta — "level 3 bots are too perfect" is unfalsifiable
  // from a log that does not say what level was being played. Re-stamped each frame so a mid-lobby
  // change is captured.
  clipMeta = { mode: 'net', size: teamSize, join: join.kind, difficulty: game.lobby().difficulty };
  scene.setTouchCue(() => sfx.softTouch()); // D44 Item 1: soft tick on a sprint knock-on
  scene.setWhiffCue(() => sfx.whoosh()); // D82: whoosh on the local player's whiffed stand tackle
  let sceneSize: number = teamSize;

  let last = performance.now();
  const frame = (now: number): void => {
    const dt = now - last;
    last = now;
    pump(now); // D117: stamp inputs + advance prediction BEFORE this frame samples the world
    perf?.frame(dt, now, game); // D65b: sample the net latency numbers (no-op unless ?debug)
    cam.frame(now); // D35: advance any keeper-gain camera ease
    scene.cameraYaw = cam.yaw;
    scene.cameraPitch = cam.pitch;
    const view = game.render(dt, now); // D117: same frame clock the pump above was given
    if (view) {
      const modeSize = view.entities.length / 2;
      if (modeSize !== sceneSize && [1, 2, 3, 5, 11].includes(modeSize)) {
        container.replaceChildren();
        scene = makeScene(modeSize as 1 | 2 | 3 | 5 | 11);
        if (clipVideo) clip.attach(scene.canvas); // D56: the rebuild swapped canvases — re-target the recording
        scene.setTouchCue(() => sfx.softTouch());
        scene.setWhiffCue(() => sfx.whoosh());
        sceneSize = modeSize;
      }
      const own = view.entities.find((e) => e.isControlled);
      // D45 Item 1: render NaN guard — a non-finite controlled pos or camera yaw ⇒ skip the camera update,
      // log once, and drop the predictor so the next snapshot re-anchors (never render into a blue void).
      const ownBad = own !== undefined && !(Number.isFinite(own.pos.x) && Number.isFinite(own.pos.y));
      if (ownBad || !Number.isFinite(cam.yaw)) {
        errorToast('[fafi] non-finite render state — re-anchoring from the next snapshot');
        game.reanchor();
      } else {
        if (own) {
          ownPos = { x: own.pos.x, y: own.pos.y }; // for the restart camera re-orient (D44)
          ownAttackSign = own.team === 0 ? view.attackDir0 : -view.attackDir0; // D130: which goal is his
        }
        lastBallPos = { x: view.ball.pos.x, y: view.ball.pos.y }; // D159: touch aim + camera focus
        const tt = touchDriving() ? touch : null;
        if (tt) {
          cam.takeFrameDx(); // drain the mouse accumulators so a device swap can't burst stale deltas
          cam.takeFrameDy();
        }
        const mdx = tt ? tt.input.takeAimDx() : cam.takeFrameDx();
        const mdy = tt ? tt.input.takeAimDy() : cam.takeFrameDy();
        // D45 Item 3b / D49: goal precision-aim on the SHOOT hold (exclusive with the magnet); else the
        // D44 aim soft-lock nudge
        mapCtx = mapContextFor(view); // D137
        touch?.frame(now, mapCtx, { charging: view.hud.chargeButton !== 0, chargeButton: view.hud.chargeButton, armed: 0 });
        const r = game.restart; // D130: the designated taker may use the precision aim without the ball
        const amTaker = r !== null && r.taker !== null && r.taker === game.controlledEntity;
        const engageHeld = tt ? tt.input.isHeld(tt.bindings.shoot) : kb.isHeld(bindings.shoot);
        goalOut = driveGoalAimLock(view, goalLock, scene, game.simCfg ?? DEFAULT_SIM_CONFIG, sceneSize as 1 | 2 | 3 | 5 | 11, engageHeld, mdx, mdy, amTaker);
        if (!goalOut.locked) {
          if (tt) {
            // D159: ball-focused camera; the opponent goal while the controlled team attacks
            const ownT = view.entities.find((e) => e.isControlled);
            const deadBallYaw =
              ownT && r !== null && (view.hud.phase === 'RESTART_PENDING' || view.hud.phase === 'KICKOFF')
                ? restartCameraYaw({
                    kind: r.kind,
                    spot: r.spot,
                    taker: r.taker,
                    controlled: game.controlledEntity,
                    ownPos,
                    attackSign: ownAttackSign,
                    pitchLength: PITCH[sceneSize as 1 | 2 | 3 | 5 | 11].length,
                  })
                : null;
            cam.yaw = tt.cam.frame({
              nowMs: now,
              dtSec: dt / 1000,
              yaw: cam.yaw,
              easing: cam.isEasing,
              manualDx: tt.input.takeCamDx(),
              recenter: tt.input.takeRecenter(),
              possession: mapCtx.ball, // D161: the camera latches attack state itself (loose holds)
              goalYaw: ownT ? Math.atan2(0 - ownT.pos.y, ownAttackSign * (PITCH[sceneSize as 1 | 2 | 3 | 5 | 11].length / 2) - ownT.pos.x) : null,
              ballYaw: ownT ? Math.atan2(view.ball.pos.y - ownT.pos.y, view.ball.pos.x - ownT.pos.x) : null,
              ballDist: ownT ? Math.hypot(view.ball.pos.x - ownT.pos.x, view.ball.pos.y - ownT.pos.y) : null,
              deadBallYaw,
            });
          } else {
            cam.yaw = aimAssist.update(view, cam.yaw, mdx, dt / 1000, now);
          }
        }
      }
      scene.cameraYaw = cam.yaw;
      updateTrajectory(scene, view, game.simCfg ?? DEFAULT_SIM_CONFIG, goalOut, touchDriving() ? lastTouchAim : cam.yaw);
      updateAerialCues(scene, view, game.simCfg ?? DEFAULT_SIM_CONFIG, now);
      updateConeCue(scene, view, game.simCfg ?? DEFAULT_SIM_CONFIG, now); // D132
      scene.setNameTags(game.humanTags()); // D43: who controls whom, over their heads
      scene.update(view);
      scene.render();
      clipMeta = { ...clipMeta, size: sceneSize, difficulty: game.lobby().difficulty };
      clipSample(view, now, goalOut.locked ? goalOut.aimYaw : touchDriving() ? lastTouchAim : cam.yaw); // D56/D130/D159
    }
    banner.frame(now);
    ceremonyCue.frame(now); // D123
    // D123: a kick pressed while the ball is still dead used to give ZERO feedback — indistinguishable
    // from a broken button. Nudge the cue instead.
    if (view?.hud.phase === 'RESTART_PENDING' && (kb.isHeld(bindings.passDirect) || kb.isHeld(bindings.shoot) || (touchDriving() && touch !== null && touch.input.anyKickHeld()))) ceremonyCue.nudge(now);
    renderLobby(); // show the lobby until kickoff, then hide it (the status marker/overlay run off a timer above)
    if (view) {
      board.update(view.hud.score, view.hud.clockDisplay, view.hud.phase, view.hud.stamina, [
        view.entities.filter((e) => e.team === 0 && e.sentOff).length,
        view.entities.filter((e) => e.team === 1 && e.sentOff).length,
      ], returnCountdown(view)); // D41 scoreboard + D55 red cards + D65c return countdown
      if (view.hud.phase === 'FULLTIME') {
        if (!endScreen.visible) {
          endScreen.show(view.hud.score);
          lock.suspend(); // release the cursor for the full-time buttons (D44 Item 9)
        }
      } else {
        endScreen.hide(); // a started rematch (phase back to KICKOFF) closes it for everyone
        lock.unsuspend();
      }
    }
    // the old one-line HUD survives behind ?debug for netcode diagnostics only (D41)
    if (params.has('debug')) {
      const invite = game.roomCode && join.kind === 'create' ? ` · invite: ${location.origin}${location.pathname}?room=${game.roomCode}` : '';
      hud.textContent = `${game.status()}${invite}`;
    }
    requestAnimationFrame(frameSafe);
  };
  // D62: the frame pump must be unkillable. A single throw anywhere in the body used to skip the
  // trailing requestAnimationFrame and end rendering PERMANENTLY — the canvas froze on its last frame
  // while the network pump, the reconnect overlay and the status marker kept running, so the tab looked
  // alive and the connection still read PLAYING. Indistinguishable, from the seat, from the netcode
  // freeze this whole programme is about.
  const frameSafe = (now: number): void => {
    try {
      frame(now);
    } catch (err) {
      errorToast(`[fafi] frame: ${err instanceof Error ? err.message : String(err)}`);
      requestAnimationFrame(frameSafe); // the body threw before it could schedule — keep the pump alive
    }
  };
  requestAnimationFrame(frameSafe);
  window.addEventListener('resize', () => scene.resize());
}

const session = new AuthSession();
// D163: ONE feedback box for every route — home's utility bar and the pause menu both open it.
const feedbackUi = mountFeedback({ doc: document, api: session.api, playerName });

// Routing (§13.1): a shared invite / create URL goes straight online (instant-play pillar). `?play` is the
// direct kickabout. Otherwise land on HOME (Play / Create online / Sign in / Teams), with a magic-link `?token`
// or an `#join=CODE` deep link handled inside the account UI.
// D138: `?lesson=<id>` plays one, `?training` opens the picker. `?drill` joins them as a direct route —
// the D128 quirk where a drill link landed on HOME first is fixed by listing it here.
const openTraining = (): void => {
  const done = makeStores(window.localStorage).tutorial.load().completed;
  const cards = LESSONS.map((l) => ({ id: l.id, title: l.title, blurb: l.blurb, icon: l.icon, done: Object.prototype.hasOwnProperty.call(done, l.id), bonus: done[l.id]?.bonus ?? false }));
  lessonPicker(
    document,
    cards,
    (id) => window.location.assign(`${window.location.pathname}?lesson=${id}`),
    () => window.location.assign(window.location.pathname),
    // D157 (C3 interim): the drills live here until the training-ground redesign handoff lands
    DRILLS.map((dr) => ({ id: dr.id, label: dr.label, blurb: dr.blurb })),
    (id) => window.location.assign(`${window.location.pathname}?drill=${encodeURIComponent(id)}`),
  );
};

if (params.has('room') || params.has('create')) runNet();
else if (params.has('training')) openTraining();
else if (params.has('lesson') || params.has('drill') || params.has('play')) runLocal();
else {
  // D157 (brand slice 1): the full-bleed home screen is the persistent landing view; sign-in/account/
  // teams are overlay flows on top of it. Play/Create hide it (the match is behind); Training, Controls
  // and the account flows overlay it. The old 560px card — with its 22-row controls block, six drill
  // buttons and labelled name field — is gone: controls live on the Controls screen (one link away),
  // drills moved to the training ground (C3), and the name is the identity chip in the utility bar.
  // flows and home reference each other; the flow callbacks stay dormant until after both exist
  const flows = createAccountFlows(session, {
    onCreateOnline: (clubTeamId) => {
      home.hide(); // "Play as this team" — the lobby takes over
      runNet({ join: { kind: 'create', teamSize: 5 }, ...(clubTeamId ? { clubTeamId } : {}) });
    },
    onStateChange: () => home.show(), // overlay closed / session refreshed → re-render the utility bar
  });
  const home = mountHomeScreen({
    doc: document,
    bindings: () => bindings, // LIVE — a rebind on the Controls screen reflects on the next show()
    newLessons: newLessonCount(makeStores(window.localStorage).tutorial.load().completed),
    playerName: {
      get: playerName,
      set: (v) => localStorage.setItem('fafi.playerName', v), // D43: used on the NEXT join
    },
    signedIn: () => session.signedIn,
    teams: () => session.api.get<{ teams: HomeTeamSummary[] }>('/me/teams').then((r) => r.teams),
    onPlay: () => runLocal(),
    onCreateOnline: () => runNet({ join: { kind: 'create', teamSize: 5 } }),
    onTraining: () => openTraining(),
    onControls: () => controlsScreen.show(),
    onSignIn: () => flows.openSignIn(),
    onMyTeams: () => flows.openTeams(),
    onFeedback: () => feedbackUi.open('home'), // D163
  });
  homeRefresh = () => home.show(); // a rebind on the Controls screen refreshes the strip live
  home.show();
  flows.boot(); // magic-link ?token / #join=CODE overlays land on top of the home screen
}
